import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { SellerAutomationHeader, SellerRunButton } from './SellerAutomationHeader';
import { SegmentedToggle } from './sellerUi';
import * as XLSX from 'xlsx';
import { apiService } from '../../services/api';
import { useSellerPromotions } from '../../hooks/useSellerPromotions';
import { getMarketplaceConnectionDisplayName } from '../../utils/marketplaceConnections';

type Marketplace = 'shopee' | 'mercadolivre';
type SellerPromotionConnection = { id: string; marketplace: Marketplace; label: string; raw: any };

const emptyNewPrice = { sku: '', name: '', shopeeBasePrice: '', shopeeCampaignPrice: '', shopeeMinPrice: '', mlBasePrice: '', mlCampaignPrice: '', mlMinPrice: '' };
const pricePageSize = 80;

function formatDate(ts?: number | null) {
  if (!ts) return '-';
  return new Date(ts * 1000).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function typeLabel(type: string) {
  const labels: Record<string, string> = {
    bundle_deal: 'Leve mais por menos',
    discount: 'Desconto',
    flash_sale: 'Oferta relampago',
    seller_campaign: 'Campanha propria',
    volume_campaign: 'Leve mais por menos',
    marketplace_campaign: 'Campanha marketplace',
    smart: 'Campanha smart',
    price_matching: 'Preco competitivo',
    price_matching_meli_all: 'Preco competitivo Mercado Livre',
    price_discount: 'Desconto de preco',
    pre_negotiated: 'Desconto pre-acordado',
    unhealthy_stock: 'Liquidacao de estoque',
    seller_coupon_campaign: 'Cupom do vendedor',
    dod: 'Oferta do dia',
    deal: 'Oferta',
  };
  return labels[type] || type;
}

function marketplaceLabel(marketplace?: string) {
  if (marketplace === 'mercadolivre') return 'Mercado Livre';
  if (marketplace === 'shopee') return 'Shopee';
  return marketplace || '';
}

function reasonLabel(reason?: string | null) {
  const labels: Record<string, string> = {
    active_or_upcoming_exists: 'Ja existe campanha ativa ou agendada',
    already_in_same_type: 'Produto ja esta nesse tipo de campanha',
    already_in_this_campaign: 'Produto ja esta nesta campanha',
    already_joined_this_run: 'Produto ja entrou em outra campanha nesta execucao',
    below_required_discount: 'Desconto minimo da oferta relampago nao atingido',
    below_min_price: 'Preco ficaria abaixo do minimo',
    campaign_full: 'Campanha sem vagas disponiveis',
    flash_rotation_cooldown: 'Aguardando rotacao da oferta relampago',
    max_flash_slots_per_run_reached: 'Limite de slots criado nesta execucao',
    missing_campaign_price: 'Preco de campanha nao informado',
    missing_candidate_details: 'Marketplace nao retornou convite elegivel para este produto',
    missing_offer_id: 'Campanha sem identificador de oferta retornado pelo Mercado Livre',
    missing_min_price: 'Preco minimo nao informado',
    ml_max_below_min_price: 'Limite do Mercado Livre abaixo do minimo',
    no_active_campaign: 'Nenhuma campanha ativa encontrada',
    no_slots_available: 'Nenhum slot disponivel',
    slot_already_has_campaign: 'Slot ja possui campanha',
  };
  return reason ? (labels[reason] || reason) : '';
}

function reportSummary(report: any) {
  if (!report) return null;
  const sync = report.sync || report.lastSyncResult || {};
  const ensure = report.ensure || report.lastEnsureCampaignsResult || report;
  const link = report.link || report.lastLinkResult || report;
  const synced = (sync?.created || 0) + (sync?.updated || 0);
  const created = ensure?.created?.length || 0;
  const skipped = (ensure?.skipped?.length || 0) + (link?.skipped?.length || 0);
  const joined = link?.joined?.length || 0;
  const failed = (ensure?.failed?.length || 0) + (link?.failed?.length || 0);
  return { synced, created, skipped, joined, failed };
}

function resultLine(item: any) {
  return [marketplaceLabel(item.marketplace), item.type ? typeLabel(item.type) : null, item.sku || item.listingId, item.campaignId || item.promotionId, reasonLabel(item.reason) || item.message].filter(Boolean).join(' - ');
}

function campaignEntryKey(item: any) {
  return [item?.marketplace, item?.connectionId, item?.type, item?.campaignId, item?.listingId, item?.modelId || '', item?.variationId || ''].map((value) => String(value ?? '')).join('|');
}

function campaignEntryPayload(item: any) {
  return {
    marketplace: item?.marketplace,
    connectionId: item?.connectionId,
    type: item?.type,
    campaignId: item?.campaignId,
    listingId: item?.listingId,
    modelId: item?.modelId || null,
    variationId: item?.variationId || null,
    priceId: item?.priceId || item?.campaignPriceId || null,
  };
}

function statusLabel(status: string) {
  if (status === 'ongoing') return 'Ativa';
  if (status === 'upcoming') return 'Agendada';
  if (status === 'expired') return 'Encerrada';
  return status || 'Sem status';
}

function headers() {
  const token = localStorage.getItem('auth_token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

function formatCurrency(value?: number | string | null) {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || n <= 0) return '-';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function listingIds(links: any[] = []) {
  return links
    .map((link) => {
      const id = link?.listingId || link?.itemId || link?.listing_id || link?.item_id || link?.externalId || link?.id;
      const variation = link?.variationId || link?.modelId || link?.variation_id || link?.model_id;
      return [id, variation ? `var ${variation}` : null].filter(Boolean).join(' / ');
    })
    .filter(Boolean);
}

function campaignLinkKey(link: any) {
  if (!link?.listingId) return '';
  return `${link.connectionId || ''}:${link.listingId}:${link.modelId || ''}:${link.variationId || ''}`;
}

function uniqueListingLinks(links: any[] = []) {
  const map = new Map<string, any>();
  for (const link of links || []) {
    const key = campaignLinkKey(link);
    if (key) map.set(key, link);
  }
  return Array.from(map.values());
}

// Vinculos que o sync descobriu mas que estao suprimidos porque alguem os removeu
// (no modal ou no bulk "Remover vinculos"). A exclusao e permanente: todo sync futuro
// respeita a chave. Sem mostrar isso na grade, a linha parece simplesmente "nao
// vinculada" e nao ha pista de que a supressao foi manual.
function suppressedListingLinks(price: any, marketplace: Marketplace) {
  const metadataField = marketplace === 'shopee' ? 'availableShopeeListingLinks' : 'availableMlListingLinks';
  const exclusionField = marketplace === 'shopee' ? 'excludedShopeeListingLinkKeys' : 'excludedMlListingLinkKeys';
  const excluded = new Set(Array.isArray(price?.metadata?.[exclusionField]) ? price.metadata[exclusionField].map(String) : []);
  if (excluded.size === 0) return [];
  const available = Array.isArray(price?.metadata?.[metadataField]) ? price.metadata[metadataField] : [];
  const activeKeys = new Set((price?.[marketplace === 'shopee' ? 'shopeeListingLinks' : 'mlListingLinks'] || []).map(campaignLinkKey));
  return uniqueListingLinks(available).filter((link: any) => {
    const key = campaignLinkKey(link);
    return key && excluded.has(key) && !activeKeys.has(key);
  });
}

function availableListingLinks(price: any, marketplace: Marketplace) {
  const activeField = marketplace === 'shopee' ? 'shopeeListingLinks' : 'mlListingLinks';
  const metadataField = marketplace === 'shopee' ? 'availableShopeeListingLinks' : 'availableMlListingLinks';
  const metadataLinks = Array.isArray(price?.metadata?.[metadataField]) ? price.metadata[metadataField] : [];
  return uniqueListingLinks([...(price?.[activeField] || []), ...metadataLinks]);
}

function MarketplaceLogo({ marketplace, ids, onClick }: { marketplace: Marketplace; ids: string[]; onClick?: () => void }) {
  if (!ids.length) return null;
  const label = marketplace === 'shopee' ? 'Shopee' : 'Mercado Livre';
  const src = marketplace === 'shopee' ? '/shopee.png' : '/mercadolivre.png';
  return (
    <button type="button" onClick={onClick} title={`${label}: ${ids.join(', ')}`} className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white shadow-sm hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300">
      <img src={src} alt={label} className="h-5 w-5 object-contain" />
    </button>
  );
}

async function readJson(res: Response) {
  const text = await res.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { success: false, message: res.ok ? 'Resposta invalida da API' : 'API indisponivel no momento' };
  }
}

type PromotionsTab = 'prices' | 'campaigns' | 'history';
const TAB_PARAM: Record<PromotionsTab, string> = { prices: 'precos', campaigns: 'campanhas', history: 'historico' };

// Tela "Promocoes": antes eram dois itens de menu (Tabela de precos e Campanhas)
// e o historico de adesoes ficava escondido no rodape da tabela. Agora sao abas.
export function SellerPromotionsView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const abaParam = searchParams.get('aba');
  const activeSection: PromotionsTab = abaParam === 'campanhas' ? 'campaigns' : abaParam === 'historico' ? 'history' : 'prices';
  const setActiveSection = (tab: PromotionsTab) => {
    const next = new URLSearchParams(searchParams);
    if (tab === 'prices') next.delete('aba'); else next.set('aba', TAB_PARAM[tab]);
    setSearchParams(next, { replace: true });
  };
  const importInputRef = useRef<HTMLInputElement | null>(null);
  const [connectionId, setConnectionId] = useState('');
  const [createConnectionId, setCreateConnectionId] = useState('');
  const [connections, setConnections] = useState<SellerPromotionConnection[]>([]);
  const [form, setForm] = useState({ type: 'discount', name: '', discountValue: '', startDateTime: '', endDateTime: '', ruleType: '', purchaseLimit: '', mlSubType: '', mlBuyQty: '', mlPayQty: '', mlDiscountPct: '', mlAllowCombination: true });
  const [bundleTiers, setBundleTiers] = useState([{ minQty: '', discountValue: '' }]);
  const [flashSlots, setFlashSlots] = useState<any[]>([]);
  const [selectedFlashSlotId, setSelectedFlashSlotId] = useState('');
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [campaignPrices, setCampaignPrices] = useState<any[]>([]);
  const [pricesLoading, setPricesLoading] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState<string | null>(null);
  const [priceSearch, setPriceSearch] = useState('');
  const [priceError, setPriceError] = useState<string | null>(null);
  const [priceNotice, setPriceNotice] = useState<string | null>(null);
  const [newPriceOpen, setNewPriceOpen] = useState(false);
  const [newPrice, setNewPrice] = useState(emptyNewPrice);
  const [showRunDetails, setShowRunDetails] = useState(false);
  const [automationRunLoading, setAutomationRunLoading] = useState(false);
  const [campaignCheckModal, setCampaignCheckModal] = useState<any | null>(null);
  const [campaignCheckSelections, setCampaignCheckSelections] = useState<Set<string>>(() => new Set());
  // A aba "Origem dos precos" saiu daqui: o mapeamento de precos do Cut App vive na
  // conexao (Nucleo de Negocio > Conexoes > ERPs > Cut App). O selo/cadeado do Cut App
  // na grade continuam nesta tela.
  const activeTab = activeSection;
  const [pricePage, setPricePage] = useState(1);
  const [selectedPriceIds, setSelectedPriceIds] = useState<Set<string>>(() => new Set());
  const [basePricePromoModal, setBasePricePromoModal] = useState<{ ids: string[]; listings: any[] } | null>(null);
  const [linkModal, setLinkModal] = useState<{ price: any; marketplace: Marketplace; links: any[]; selectedKeys: string[] } | null>(null);
  const [joinLogs, setJoinLogs] = useState<any[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  // null = ainda nao sabemos se o tenant tem Cut App. Enquanto for null a UI da
  // integracao fica escondida (evita piscar para quem nunca vai usar).
  const [cutappConnected, setCutappConnected] = useState<boolean | null>(null);

  const selectedConnection = connections.find((c) => c.id === connectionId) || null;
  const createConnection = connections.find((c) => c.id === createConnectionId) || null;
  const createMarketplace = createConnection?.marketplace || 'shopee';
  const effectiveConnectionId = selectedConnection?.id || '';
  const { promotions, settings, loading, saving, error, syncPromotions, createPromotion, runAutomation, checkCampaigns } = useSellerPromotions({ connectionId: effectiveConnectionId || undefined });

  const loadCampaignPrices = async () => {
    setPricesLoading(true);
    setPriceError(null);
    setPriceNotice(null);
    try {
      const res = await fetch('/api/seller/promotions/prices', { headers: headers() });
      const json = await readJson(res);
      if (!json.success) throw new Error(json.message || 'Erro ao buscar tabela de precos');
      setCampaignPrices(json.prices || []);
    } catch (err: any) {
      setPriceError(err.message || 'Erro ao buscar tabela de precos');
    } finally {
      setPricesLoading(false);
    }
  };

  const loadJoinLogs = async () => {
    setLogsLoading(true);
    try {
      const res = await fetch('/api/seller/promotions/logs?limit=40', { headers: headers() });
      const json = await readJson(res);
      if (json.success) setJoinLogs(json.logs || []);
    } finally {
      setLogsLoading(false);
    }
  };

  const syncCampaignPrices = async () => {
    setPricesLoading(true);
    setPriceError(null);
    setPriceNotice(null);
    try {
      const res = await fetch('/api/seller/promotions/prices/sync', { method: 'POST', headers: headers() });
      const json = await readJson(res);
      if (!json.success) throw new Error(json.message || 'Erro ao importar produtos do Business Core');
      await loadCampaignPrices();
    } catch (err: any) {
      setPriceError(err.message || 'Erro ao importar produtos do Business Core');
      setPricesLoading(false);
    }
  };

  const setLocalPrice = (id: string, field: string, value: string | boolean) => {
    setCampaignPrices((items) => items.map((item) => item.id === id ? { ...item, [field]: value } : item));
  };

  const savePriceField = async (id: string, field: string, value: any) => {
    const res = await fetch(`/api/seller/promotions/prices/${encodeURIComponent(id)}`, { method: 'PATCH', headers: headers(), body: JSON.stringify({ [field]: value }) });
    const json = await readJson(res);
    if (!json.success) throw new Error(json.message || 'Erro ao salvar preco');
    setCampaignPrices((items) => items.map((item) => item.id === id ? json.price : item));
    return json.price;
  };

  // Cadeado por campo: grava price.priceOverrides = { [campo]: true }.
  // Campo travado nunca e sobrescrito pelos syncs de preco (Business Core e Cut App).
  const togglePriceOverride = async (price: any, field: string) => {
    const current = { ...(price.priceOverrides || {}) };
    if (current[field]) delete current[field]; else current[field] = true;
    setCampaignPrices((items) => items.map((item) => item.id === price.id ? { ...item, priceOverrides: current } : item));
    try {
      await savePriceField(price.id, 'priceOverrides', current);
    } catch (err: any) {
      setPriceError(err.message || 'Erro ao travar o campo');
    }
  };

  const openLinkModal = (price: any, marketplace: Marketplace) => {
    const activeLinks = marketplace === 'shopee' ? (price.shopeeListingLinks || []) : (price.mlListingLinks || []);
    setLinkModal({ price, marketplace, links: availableListingLinks(price, marketplace), selectedKeys: activeLinks.map(campaignLinkKey).filter(Boolean) });
  };

  const saveLinkModal = async () => {
    if (!linkModal) return;
    const field = linkModal.marketplace === 'shopee' ? 'shopeeListingLinks' : 'mlListingLinks';
    const selected = linkModal.links.filter((link: any) => linkModal.selectedKeys.includes(campaignLinkKey(link)));
    try {
      await savePriceField(linkModal.price.id, field, selected);
      setLinkModal(null);
    } catch (err: any) {
      setPriceError(err.message || 'Erro ao salvar vinculos');
    }
  };

  const createCampaignPriceRow = async () => {
    if (!newPrice.sku.trim()) { setPriceError('Informe o SKU para adicionar a linha'); return; }
    setPricesLoading(true);
    setPriceError(null);
    try {
      const res = await fetch('/api/seller/promotions/prices', { method: 'POST', headers: headers(), body: JSON.stringify(newPrice) });
      const json = await readJson(res);
      if (!json.success) throw new Error(json.message || 'Erro ao adicionar linha');
      setCampaignPrices((items) => [json.price, ...items.filter((item) => item.id !== json.price.id)]);
      setNewPrice(emptyNewPrice);
      setNewPriceOpen(false);
    } catch (err: any) {
      setPriceError(err.message || 'Erro ao adicionar linha');
    } finally {
      setPricesLoading(false);
    }
  };

  const deleteCampaignPriceRow = async (id: string) => {
    if (!window.confirm('Remover esta linha da tabela de precos?')) return;
    setPriceError(null);
    setPriceNotice(null);
    try {
      const res = await fetch(`/api/seller/promotions/prices/${encodeURIComponent(id)}`, { method: 'DELETE', headers: headers() });
      const json = await readJson(res);
      if (!json.success) throw new Error(json.message || 'Erro ao remover linha');
      setCampaignPrices((items) => items.filter((item) => item.id !== id));
      setSelectedPriceIds((current) => {
        const next = new Set(current);
        next.delete(id);
        return next;
      });
    } catch (err: any) {
      setPriceError(err.message || 'Erro ao remover linha');
    }
  };

  const mergeCampaignPriceRows = (prices: any[] = []) => {
    if (!prices.length) return;
    const byId = new Map(prices.map((price) => [price.id, price]));
    setCampaignPrices((items) => items.map((item) => byId.get(item.id) || item));
  };

  const selectedPriceIdList = () => Array.from(selectedPriceIds);

  const bulkRemoveLinks = async () => {
    const ids = selectedPriceIdList();
    if (ids.length === 0) return;
    if (!window.confirm(`Remover todos os vinculos dos ${ids.length} produto(s) selecionados?`)) return;
    setBulkActionLoading('remove-links');
    setPriceError(null);
    setPriceNotice(null);
    try {
      const res = await fetch('/api/seller/promotions/prices/bulk/remove-links', { method: 'POST', headers: headers(), body: JSON.stringify({ ids }) });
      const json = await readJson(res);
      if (!json.success) throw new Error(json.message || 'Erro ao remover vinculos');
      mergeCampaignPriceRows(json.prices || []);
      setSelectedPriceIds(new Set());
      setPriceNotice(`${json.updated || 0} produto(s) tiveram os vinculos removidos.`);
    } catch (err: any) {
      setPriceError(err.message || 'Erro ao remover vinculos');
    } finally {
      setBulkActionLoading(null);
    }
  };

  // Shopee e ML recusam subir o preco Base de anuncio em promocao. Primeiro
  // consultamos as promocoes; se houver, o usuario decide no modal se removemos.
  const runBasePriceUpdate = async (ids: string[], removePromotions: boolean) => {
    setBulkActionLoading('base-price');
    setPriceError(null);
    setPriceNotice(null);
    try {
      const res = await fetch('/api/seller/promotions/prices/bulk/update-base-marketplace', { method: 'POST', headers: headers(), body: JSON.stringify({ ids, removePromotions }) });
      const json = await readJson(res);
      if (!json.success) throw new Error(json.message || 'Erro ao atualizar preco Base no marketplace');
      mergeCampaignPriceRows(json.prices || []);
      const skipped = json.skipped || [];
      const inPromotion = skipped.filter((item: any) => item.reason === 'in_promotion').length;
      const parts = [`${(json.updated || []).length} anuncio(s) atualizados, ${(json.failed || []).length} falha(s), ${skipped.length} ignorado(s).`];
      if ((json.removedPromotions || []).length > 0) parts.push(`${json.removedPromotions.length} promocao(oes) removida(s).`);
      if (inPromotion > 0) parts.push(`${inPromotion} anuncio(s) mantidos por estarem em promocao.`);
      if ((json.failedRemovals || []).length > 0) parts.push(`${json.failedRemovals.length} promocao(oes) nao puderam ser removidas.`);
      setPriceNotice(parts.join(' '));
      const failed = json.failed || [];
      if (failed.length > 0) setPriceError(failed.slice(0, 3).map((item: any) => `${item.sku} (${item.marketplace === 'shopee' ? 'Shopee' : 'ML'} ${item.listingId}): ${item.message}`).join(' | '));
    } catch (err: any) {
      setPriceError(err.message || 'Erro ao atualizar preco Base no marketplace');
    } finally {
      setBulkActionLoading(null);
    }
  };

  const bulkUpdateBasePriceOnMarketplace = async () => {
    const ids = selectedPriceIdList();
    if (ids.length === 0) return;
    setBulkActionLoading('base-price-check');
    setPriceError(null);
    setPriceNotice(null);
    let check: any;
    try {
      const res = await fetch('/api/seller/promotions/prices/bulk/check-base-promotions', { method: 'POST', headers: headers(), body: JSON.stringify({ ids }) });
      check = await readJson(res);
      if (!check.success) throw new Error(check.message || 'Erro ao verificar promocoes dos anuncios');
    } catch (err: any) {
      setPriceError(err.message || 'Erro ao verificar promocoes dos anuncios');
      setBulkActionLoading(null);
      return;
    }
    setBulkActionLoading(null);
    const listings = Array.isArray(check.listings) ? check.listings : [];
    if (listings.length > 0) {
      setBasePricePromoModal({ ids, listings });
      return;
    }
    if (!window.confirm(`Atualizar o preco Base no marketplace dos ${ids.length} produto(s) selecionados? Nenhum anuncio esta em promocao.`)) return;
    await runBasePriceUpdate(ids, false);
  };

  const bulkDeletePrices = async () => {
    const ids = selectedPriceIdList();
    if (ids.length === 0) return;
    if (!window.confirm(`Deletar completamente os ${ids.length} preco(s) selecionados?`)) return;
    setBulkActionLoading('delete');
    setPriceError(null);
    setPriceNotice(null);
    try {
      const res = await fetch('/api/seller/promotions/prices/bulk/delete', { method: 'POST', headers: headers(), body: JSON.stringify({ ids }) });
      const json = await readJson(res);
      if (!json.success) throw new Error(json.message || 'Erro ao deletar precos');
      setCampaignPrices((items) => items.filter((item) => !selectedPriceIds.has(item.id)));
      setSelectedPriceIds(new Set());
      setPriceNotice(`${json.deleted || 0} preco(s) deletados.`);
    } catch (err: any) {
      setPriceError(err.message || 'Erro ao deletar precos');
    } finally {
      setBulkActionLoading(null);
    }
  };

  const csvEscape = (value: any) => {
    const text = String(value ?? '');
    return /[",;\n]/.test(text) ? '"' + text.replace(/"/g, '""') + '"' : text;
  };

  const exportCampaignPrices = () => {
    const columns = [
      ['Nome', 'name'],
      ['SKU', 'sku'],
      ['Shopee Base', 'shopeeBasePrice'],
      ['Preco Shopee', 'shopeeCampaignPrice'],
      ['Shopee Minimo', 'shopeeMinPrice'],
      ['ML Base', 'mlBasePrice'],
      ['Preco ML', 'mlCampaignPrice'],
      ['ML Minimo', 'mlMinPrice'],
      ['Ativo', 'enabled'],
    ];
    const templateRow = { name: 'Produto exemplo', sku: 'SKU-001', shopeeBasePrice: '', shopeeCampaignPrice: '', shopeeMinPrice: '', mlBasePrice: '', mlCampaignPrice: '', mlMinPrice: '', enabled: true };
    const rows = campaignPrices.length > 0 ? campaignPrices : [templateRow];
    const csv = [
      columns.map(([label]) => csvEscape(label)).join(';'),
      ...rows.map((row: any) => columns.map(([, key]) => csvEscape(key === 'enabled' ? (row[key] === false ? 'false' : 'true') : row[key])).join(';')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'precos-campanhas.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportCampaignPricesXlsx = () => {
    const rows = (campaignPrices.length > 0 ? campaignPrices : [{ name: 'Produto exemplo', sku: 'SKU-001', shopeeBasePrice: '', shopeeCampaignPrice: '', shopeeMinPrice: '', mlBasePrice: '', mlCampaignPrice: '', mlMinPrice: '', enabled: true }]).map((row: any) => ({
      Nome: row.name || '',
      SKU: row.sku || '',
      'Shopee Base': row.shopeeBasePrice ?? '',
      'Preco Shopee': row.shopeeCampaignPrice ?? '',
      'Shopee Minimo': row.shopeeMinPrice ?? '',
      'ML Base': row.mlBasePrice ?? '',
      'Preco ML': row.mlCampaignPrice ?? '',
      'ML Minimo': row.mlMinPrice ?? '',
      Ativo: row.enabled === false ? 'false' : 'true',
    }));
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Precos');
    XLSX.writeFile(workbook, 'precos-campanhas.xlsx');
  };

  const parseCsvLine = (line: string, delimiter: string) => {
    const cells: string[] = [];
    let current = '';
    let quoted = false;
    for (let i = 0; i < line.length; i += 1) {
      const char = line[i];
      if (char === '"' && quoted && line[i + 1] === '"') { current += '"'; i += 1; continue; }
      if (char === '"') { quoted = !quoted; continue; }
      if (char === delimiter && !quoted) { cells.push(current.trim()); current = ''; continue; }
      current += char;
    }
    cells.push(current.trim());
    return cells;
  };

  const parseCsv = (text: string) => {
    const lines = text.replace(/^\uFEFF/, '').split(/\r?\n/).filter((line) => line.trim());
    if (lines.length < 2) return [];
    const delimiter = (lines[0].match(/;/g) || []).length >= (lines[0].match(/,/g) || []).length ? ';' : ',';
    const header = parseCsvLine(lines[0], delimiter);
    return lines.slice(1).map((line) => {
      const cells = parseCsvLine(line, delimiter);
      return header.reduce((row: any, key, index) => ({ ...row, [key]: cells[index] ?? '' }), {});
    });
  };

  const parseSpreadsheet = async (file: File) => {
    const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
    const firstSheetName = workbook.SheetNames[0];
    if (!firstSheetName) return [];
    return XLSX.utils.sheet_to_json(workbook.Sheets[firstSheetName], { defval: '' });
  };

  const importCampaignPricesFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setPricesLoading(true);
    setPriceError(null);
    try {
      const lowerName = file.name.toLowerCase();
      const rows = lowerName.endsWith('.xlsx') || lowerName.endsWith('.xls') ? await parseSpreadsheet(file) : parseCsv(await file.text());
      if (rows.length === 0) throw new Error('Arquivo sem linhas validas');
      const res = await fetch('/api/seller/promotions/prices/import', { method: 'POST', headers: headers(), body: JSON.stringify({ rows }) });
      const json = await readJson(res);
      if (!json.success) throw new Error(json.message || 'Erro ao importar precos');
      await loadCampaignPrices();
    } catch (err: any) {
      setPriceError(err.message || 'Erro ao importar precos');
      setPricesLoading(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      const [shopeeRes, mlRes] = await Promise.all([
        apiService.listShopeeConnections().catch(() => ({ data: [] })),
        apiService.listMercadoLivreConnections().catch(() => ({ data: [] })),
      ]);
      const shopee = (shopeeRes.data || []).map((c: any) => ({
        id: c.id,
        marketplace: 'shopee' as const,
        label: `Shopee - ${getMarketplaceConnectionDisplayName('shopee', c)}`,
        raw: c,
      }));
      const ml = (mlRes.data || []).map((c: any) => ({
        id: c.id,
        marketplace: 'mercadolivre' as const,
        label: `Mercado Livre - ${getMarketplaceConnectionDisplayName('mercadolivre', c)}`,
        raw: c,
      }));
      const all = [...shopee, ...ml];
      setConnections(all);
      setConnectionId((current) => current && all.some((c) => c.id === current) ? current : '');
      setCreateConnectionId((current) => current && all.some((c) => c.id === current) ? current : '');
    };
    load().catch(() => setConnections([]));
  }, []);

  useEffect(() => {
    loadCampaignPrices().catch(() => undefined);
    loadJoinLogs().catch(() => undefined);
  }, []);

  // Uma unica consulta ao estado da conexao Cut App. Nao ter conexao e o caso
  // normal da maioria dos tenants: sem erro, sem toast, sem log no console.
  useEffect(() => {
    let cancelled = false;
    apiService
      .getCutappConnection()
      .then((res: any) => {
        if (cancelled) return;
        const connection = res?.connection ?? (res?.baseUrl != null ? res : null);
        setCutappConnected(!!connection);
      })
      .catch(() => {
        if (!cancelled) setCutappConnected(false);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setForm((current) => ({
      ...current,
      type: createMarketplace === 'mercadolivre'
        ? (['seller_campaign', 'volume_campaign'].includes(current.type) ? current.type : 'seller_campaign')
        : (['discount', 'bundle_deal', 'flash_sale'].includes(current.type) ? current.type : 'discount'),
    }));
  }, [createMarketplace]);

  useEffect(() => {
    if (createMarketplace !== 'shopee' || form.type !== 'flash_sale' || !createConnectionId) {
      setFlashSlots([]);
      setSelectedFlashSlotId('');
      return;
    }
    setLoadingSlots(true);
    fetch(`/api/seller/promotions/shopee/flash-slots?connectionId=${encodeURIComponent(createConnectionId)}&flashHorizonHours=168`, { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}` } })
      .then((res) => res.json())
      .then((json) => {
        const slots = json.success ? (json.slots || []) : [];
        setFlashSlots(slots);
        setSelectedFlashSlotId((current) => slots.some((slot: any) => String(slot.timeSlotId) === current && slot.available) ? current : '');
      })
      .catch(() => setFlashSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [createMarketplace, form.type, createConnectionId]);

  const visiblePromotions = useMemo(() => promotions.filter((p) => p.status === 'ongoing' || p.status === 'upcoming'), [promotions]);
  const filteredCampaignPrices = useMemo(() => {
    const q = priceSearch.trim().toLowerCase();
    if (!q) return campaignPrices;
    return campaignPrices.filter((price) => String(price.sku || '').toLowerCase().includes(q) || String(price.name || '').toLowerCase().includes(q));
  }, [campaignPrices, priceSearch]);
  const pricePageCount = Math.max(1, Math.ceil(filteredCampaignPrices.length / pricePageSize));
  const currentPricePage = Math.min(pricePage, pricePageCount);
  const pricePageStart = filteredCampaignPrices.length === 0 ? 0 : ((currentPricePage - 1) * pricePageSize) + 1;
  const pricePageEnd = Math.min(currentPricePage * pricePageSize, filteredCampaignPrices.length);
  const visibleCampaignPrices = filteredCampaignPrices.slice((currentPricePage - 1) * pricePageSize, currentPricePage * pricePageSize);
  const selectedPriceCount = selectedPriceIds.size;
  const visiblePriceIds = visibleCampaignPrices.map((price) => price.id).filter(Boolean);
  const allVisiblePricesSelected = visiblePriceIds.length > 0 && visiblePriceIds.every((id) => selectedPriceIds.has(id));
  const someVisiblePricesSelected = visiblePriceIds.some((id) => selectedPriceIds.has(id));
  const readyForAutomation = useMemo(() => campaignPrices.filter((price) => (
    (Number(price.shopeeMinPrice || 0) > 0 && (!Number(price.shopeeCampaignPrice || 0) || Number(price.shopeeCampaignPrice || 0) >= Number(price.shopeeMinPrice || 0)) && (price.shopeeListingLinks || []).length > 0)
    || (Number(price.mlMinPrice || 0) > 0 && (!Number(price.mlCampaignPrice || 0) || Number(price.mlCampaignPrice || 0) >= Number(price.mlMinPrice || 0)) && (price.mlListingLinks || []).length > 0)
  )).length, [campaignPrices]);
  const expiredCount = useMemo(() => promotions.filter((p) => p.status === 'expired').length, [promotions]);
  // Linhas que ja vieram do Cut App continuam se identificando mesmo que a conexao
  // tenha sido apagada depois: o dado e real e explica de onde veio o preco.
  const hasCutappPrices = useMemo(() => campaignPrices.some((price) => price.priceSource === 'cutapp'), [campaignPrices]);
  const showCutappUi = cutappConnected === true || hasCutappPrices;

  useEffect(() => {
    setPricePage(1);
  }, [priceSearch]);

  useEffect(() => {
    setPricePage((page) => Math.min(page, pricePageCount));
  }, [pricePageCount]);

  useEffect(() => {
    const valid = new Set(campaignPrices.map((price) => price.id));
    setSelectedPriceIds((current) => new Set(Array.from(current).filter((id) => valid.has(id))));
  }, [campaignPrices]);

  const togglePriceSelection = (id: string, checked: boolean) => {
    setSelectedPriceIds((current) => {
      const next = new Set(current);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };

  const toggleVisiblePriceSelection = (checked: boolean) => {
    setSelectedPriceIds((current) => {
      const next = new Set(current);
      for (const id of visiblePriceIds) {
        if (checked) next.add(id); else next.delete(id);
      }
      return next;
    });
  };

  const selectAllFilteredPrices = () => {
    setSelectedPriceIds(new Set(filteredCampaignPrices.map((price) => price.id).filter(Boolean)));
  };

  const submit = async () => {
    const start = form.startDateTime ? Math.floor(new Date(form.startDateTime).getTime() / 1000) : undefined;
    const end = form.endDateTime ? Math.floor(new Date(form.endDateTime).getTime() / 1000) : undefined;
    await createPromotion({
      marketplace: createMarketplace,
      connectionId: createConnectionId,
      type: form.type,
      name: form.name.trim(),
      startTime: start,
      endTime: end,
      startDateLocal: form.startDateTime,
      endDateLocal: form.endDateTime,
      timeSlotId: form.type === 'flash_sale' ? selectedFlashSlotId : undefined,
      discountValue: Number(form.discountValue || form.mlDiscountPct || 0),
      ruleType: Number(form.ruleType || 2),
      purchaseLimit: form.purchaseLimit ? Number(form.purchaseLimit) : undefined,
      bundleTiers: form.type === 'bundle_deal' ? bundleTiers.map((tier) => ({ minQty: Number(tier.minQty || 0), discountValue: Number(tier.discountValue || 0) })).filter((tier) => tier.minQty > 0 && tier.discountValue > 0) : undefined,
      mlSubType: form.type === 'volume_campaign' ? form.mlSubType : undefined,
      mlBuyQty: Number(form.mlBuyQty || 0),
      mlPayQty: Number(form.mlPayQty || 0),
      mlDiscountPct: Number(form.mlDiscountPct || 0),
      mlAllowCombination: true,
    });
  };

  const syncPromotionsNow = async () => {
    try {
      const result = await syncPromotions();
      toast.success(`${Number(result.count || 0)} campanha(s) sincronizada(s) e salvas no banco.`);
    } catch (err: any) {
      toast.error(err.message || 'Erro ao sincronizar campanhas');
    }
  };

  const runAutomationNow = async () => {
    setAutomationRunLoading(true);
    try {
      const json = await runAutomation({ mode: 'create', triggeredBy: 'manual_campaign_creation' } as any);
      const result = json.result || json;
      const summary = reportSummary(result);
      if (summary) {
        toast.success(`Execução concluída: ${summary.created} campanha(s) criada(s) e ${summary.failed} falha(s).`);
      } else {
        toast.success('Criação automática executada.');
      }
      await loadCampaignPrices().catch(() => undefined);
      await loadJoinLogs().catch(() => undefined);
    } catch (err: any) {
      toast.error(err.message || 'Erro ao executar a criação de campanhas');
    } finally {
      setAutomationRunLoading(false);
    }
  };

  const openCampaignCheckModal = () => {
    const ids = selectedPriceIdList();
    if (ids.length === 0) {
      setPriceError('Selecione ao menos um produto para verificar campanhas.');
      return;
    }
    setPriceError(null);
    setCampaignCheckSelections(new Set());
    setCampaignCheckModal({ step: 'idle', priceIds: ids });
  };

  const runCampaignCheckPreview = async () => {
    const ids = campaignCheckModal?.priceIds || selectedPriceIdList();
    if (!ids.length) return;
    setCampaignCheckModal((current: any) => ({ ...(current || {}), step: 'checking', priceIds: ids }));
    const json = await checkCampaigns({ priceIds: ids } as any);
    const result = json.result || json;
    const planned = Array.isArray(result?.link?.planned) ? result.link.planned : [];
    setCampaignCheckSelections(new Set(planned.map(campaignEntryKey)));
    setCampaignCheckModal({ step: 'results', priceIds: ids, result });
    await loadCampaignPrices().catch(() => undefined);
    await loadJoinLogs().catch(() => undefined);
  };

  const authorizeCampaignCheck = async () => {
    const ids = campaignCheckModal?.priceIds || selectedPriceIdList();
    const planned = Array.isArray(campaignCheckModal?.result?.link?.planned) ? campaignCheckModal.result.link.planned : [];
    const approvedEntries = planned.filter((item: any) => campaignCheckSelections.has(campaignEntryKey(item))).map(campaignEntryPayload);
    if (!ids.length || approvedEntries.length === 0) return;
    setCampaignCheckModal((current: any) => ({ ...(current || {}), step: 'joining' }));
    const json = await checkCampaigns({ confirm: true, priceIds: ids, approvedEntries } as any);
    setCampaignCheckModal({ step: 'done', priceIds: ids, result: json.result || json, confirmed: true });
    await loadCampaignPrices().catch(() => undefined);
    await loadJoinLogs().catch(() => undefined);
  };

  // Resumo da ultima rodada da automacao (criadas / adesoes / ignoradas / falhas).
  // Esse bloco existia so dentro de um `{false && ...}` — ou seja, estava pronto
  // e nunca aparecia para ninguem. Agora alimenta a prop `result` do cabecalho.
  const renderLastRunSummary = () => {
    const runReport = settings?.lastRunResult || settings?.lastLinkResult || settings?.lastEnsureCampaignsResult;
    const summary = reportSummary(runReport);
    if (!summary) return null;
    const sync = runReport?.sync || runReport?.lastSyncResult || {};
    const ensure = runReport?.ensure || runReport?.lastEnsureCampaignsResult || runReport;
    const link = runReport?.link || runReport?.lastLinkResult || runReport;
    const details = [...(ensure?.failed || []), ...(link?.failed || []), ...(link?.skipped || []), ...(ensure?.created || []), ...(link?.joined || [])].slice(0, 30);
    return (
      <div className="space-y-2">
        <div className="grid grid-cols-2 gap-2 text-center text-xs md:grid-cols-5">
          <div className="rounded-md bg-sky-50 px-2 py-2 text-sky-700"><div className="font-semibold">{summary.synced}</div><div>Tabela</div></div>
          <div className="rounded-md bg-blue-50 px-2 py-2 text-blue-700"><div className="font-semibold">{summary.created}</div><div>Criadas</div></div>
          <div className="rounded-md bg-emerald-50 px-2 py-2 text-emerald-700"><div className="font-semibold">{summary.joined}</div><div>Adesoes</div></div>
          <div className="rounded-md bg-gray-50 px-2 py-2 text-gray-600"><div className="font-semibold">{summary.skipped}</div><div>Ignoradas</div></div>
          <div className="rounded-md bg-red-50 px-2 py-2 text-red-700"><div className="font-semibold">{summary.failed}</div><div>Falhas</div></div>
        </div>
        {(sync?.created != null || sync?.updated != null || sync?.skipped != null) && <p className="text-xs text-gray-500">Tabela: {sync.created || 0} novas, {sync.updated || 0} atualizadas, {sync.skipped || 0} ignoradas.</p>}
        {details.length > 0 && <div>
          <button type="button" onClick={() => setShowRunDetails((open) => !open)} className="text-xs font-medium text-gray-700 hover:text-gray-900">{showRunDetails ? 'Ocultar detalhes' : 'Ver detalhes da ultima execucao'}</button>
          {showRunDetails && <div className="mt-2 max-h-56 overflow-y-auto rounded-md border border-gray-200 bg-gray-50 p-2 text-xs text-gray-700">{details.map((item: any, index: number) => <div key={index} className="border-b border-gray-200 py-1 last:border-0">{resultLine(item)}</div>)}</div>}
        </div>}
      </div>
    );
  };

  const renderJoinHistory = () => (
    <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Historico de adesoes</h2>
          <p className="text-xs text-gray-500">Tentativas dos anuncios entrando em campanhas a partir da tabela de precos.</p>
        </div>
      </div>
      <div className="overflow-x-auto rounded-md border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs font-semibold uppercase text-gray-500">
            <tr><th className="px-3 py-2">Quando</th><th className="px-3 py-2">Produto</th><th className="px-3 py-2">Canal</th><th className="px-3 py-2">Campanha</th><th className="px-3 py-2">Preco</th><th className="px-3 py-2">Status</th></tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {logsLoading && <tr><td colSpan={6} className="px-3 py-5 text-center text-gray-500">Carregando historico...</td></tr>}
            {!logsLoading && joinLogs.length === 0 && <tr><td colSpan={6} className="px-3 py-5 text-center text-gray-500">Nenhuma tentativa registrada ainda.</td></tr>}
            {!logsLoading && joinLogs.map((log) => (
              <tr key={log.id}>
                <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-500">{log.createdAt ? new Date(log.createdAt).toLocaleString('pt-BR') : '-'}</td>
                <td className="px-3 py-2"><div className="font-medium text-gray-900">{log.productName || log.sku || log.listingId}</div><div className="text-xs text-gray-500">{log.sku ? `SKU ${log.sku}` : log.listingId}</div></td>
                <td className="px-3 py-2 text-gray-700">{marketplaceLabel(log.marketplace)}</td>
                <td className="px-3 py-2"><div className="text-gray-900">{log.campaignName || typeLabel(log.campaignType)}</div><div className="text-xs text-gray-500">{typeLabel(log.campaignType)}</div></td>
                <td className="px-3 py-2 text-gray-700">{formatCurrency(log.joinedPrice)}</td>
                <td className="px-3 py-2"><span className={`rounded-full px-2 py-1 text-xs font-semibold ${log.status === 'joined' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>{log.status === 'joined' ? 'Adicionado' : 'Falhou'}</span>{log.reason && <div className="mt-1 max-w-xs text-xs text-gray-500">{reasonLabel(log.reason)}</div>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );

  return (
    <div className="space-y-5">
      <input ref={importInputRef} type="file" accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel" onChange={importCampaignPricesFile} className="hidden" />

      <SellerAutomationHeader
        title="Promoções"
        subtitle="Defina o preço de campanha de cada produto; o sistema entra nas promoções dos marketplaces respeitando esses preços."
        automationKeys={['promotions-join', 'promotions-sync', 'promotions-create']}
        actions={activeTab === 'prices' ? <>
          <SellerRunButton
            label={selectedPriceCount > 0 ? `Verificar e aderir (${selectedPriceCount})` : 'Selecione produtos para aderir'}
            variant="primary"
            onClick={openCampaignCheckModal}
            disabled={saving || pricesLoading || selectedPriceCount === 0}
          />
        </> : activeTab === 'campaigns' ? <>
          <SellerRunButton label="Sincronizar campanhas" runningLabel="Sincronizando..." running={saving} onClick={syncPromotionsNow} variant="primary" />
        </> : <>
          <SellerRunButton label="Atualizar" runningLabel="Atualizando..." running={logsLoading} onClick={loadJoinLogs} />
        </>}
        more={activeTab === 'prices' ? [
          { label: 'Importar do Business Core', hint: 'Traz os produtos cadastrados para a tabela', disabled: pricesLoading, onClick: syncCampaignPrices },
          { label: 'Adicionar linha', onClick: () => setNewPriceOpen((open) => !open) },
          { label: 'Importar CSV/XLSX', disabled: pricesLoading, onClick: () => importInputRef.current?.click() },
          { label: 'Exportar CSV', onClick: exportCampaignPrices },
          { label: 'Exportar XLSX', onClick: exportCampaignPricesXlsx },
        ] : activeTab === 'campaigns' ? [
          { label: 'Criar campanhas agora', hint: 'Roda a criação automática dos tipos ligados nas Automações', disabled: saving || automationRunLoading, onClick: runAutomationNow },
        ] : []}
        result={renderLastRunSummary()}
      />

      <SegmentedToggle<PromotionsTab>
        value={activeTab}
        onChange={setActiveSection}
        options={[{ value: 'prices', label: 'Tabela de preços' }, { value: 'campaigns', label: 'Campanhas' }, { value: 'history', label: 'Histórico de adesões' }]}
      />

      {activeTab === 'prices' && <>
      <section className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Tabela de precos para campanhas</h2>
            <p className="text-xs text-gray-500">Importar do Business Core cria/atualiza linhas a partir dos produtos e anuncios ja vinculados; a automacao so usa linhas com preco minimo valido.</p>
            {showCutappUi
              ? <p className="mt-1 text-xs text-gray-400">Linhas com o selo <span className="font-semibold text-purple-700">Cut App</span> tem preco de origem no Cut App. Use o cadeado 🔒 em cada campo para travar o valor: campo travado nunca e sobrescrito pelo sync.</p>
              : <p className="mt-1 text-xs text-gray-400">Use o cadeado 🔒 em cada campo para travar o valor: campo travado nao e sobrescrito pela sincronizacao de precos.</p>}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">{readyForAutomation} prontos</span>
            {filteredCampaignPrices.length > 0 && <button type="button" onClick={selectAllFilteredPrices} className="rounded-md border border-gray-300 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50">Selecionar todos</button>}
            <input value={priceSearch} onChange={(e) => setPriceSearch(e.target.value)} className="w-full sm:w-64 rounded-md border border-gray-300 px-3 py-2 text-sm" placeholder="Buscar SKU ou produto" />
          </div>
        </div>
        {priceError && <div className="mx-4 mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{priceError}</div>}
        {priceNotice && <div className="mx-4 mt-3 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{priceNotice}</div>}
        {selectedPriceCount > 0 && <div className="mx-4 mt-3 flex flex-wrap items-center justify-between gap-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-3">
          <span className="text-sm font-medium text-gray-700">{selectedPriceCount} produto(s) selecionado(s)</span>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={bulkRemoveLinks} disabled={!!bulkActionLoading} className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-white disabled:opacity-50">{bulkActionLoading === 'remove-links' ? 'Removendo...' : 'Remover vinculos'}</button>
            <button type="button" onClick={bulkUpdateBasePriceOnMarketplace} disabled={!!bulkActionLoading} className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-white disabled:opacity-50">{bulkActionLoading === 'base-price-check' ? 'Verificando promocoes...' : bulkActionLoading === 'base-price' ? 'Atualizando...' : 'Atualizar preco Base no marketplace'}</button>
            <button type="button" onClick={bulkDeletePrices} disabled={!!bulkActionLoading} className="rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50">{bulkActionLoading === 'delete' ? 'Deletando...' : 'Deletar precos'}</button>
            <button type="button" onClick={() => setSelectedPriceIds(new Set())} disabled={!!bulkActionLoading} className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-white disabled:opacity-50">Limpar selecao</button>
          </div>
        </div>}
        {newPriceOpen && <div className="mx-4 mt-3 rounded-md border border-gray-200 bg-gray-50 p-3">
          <div className="grid gap-2 md:grid-cols-7">
            <input value={newPrice.sku} onChange={(e) => setNewPrice({ ...newPrice, sku: e.target.value })} className="min-w-0 rounded-md border border-gray-300 px-3 py-2 text-sm md:col-span-1" placeholder="SKU" />
            <input value={newPrice.name} onChange={(e) => setNewPrice({ ...newPrice, name: e.target.value })} className="min-w-0 rounded-md border border-gray-300 px-3 py-2 text-sm md:col-span-2" placeholder="Produto" />
            <input value={newPrice.shopeeBasePrice} onChange={(e) => setNewPrice({ ...newPrice, shopeeBasePrice: e.target.value })} className="min-w-0 rounded-md border border-gray-300 px-3 py-2 text-sm" placeholder="Shopee base" inputMode="decimal" />
            <input value={newPrice.shopeeCampaignPrice} onChange={(e) => setNewPrice({ ...newPrice, shopeeCampaignPrice: e.target.value })} className="min-w-0 rounded-md border border-gray-300 px-3 py-2 text-sm" placeholder="Shopee campanha" inputMode="decimal" />
            <input value={newPrice.shopeeMinPrice} onChange={(e) => setNewPrice({ ...newPrice, shopeeMinPrice: e.target.value })} className="min-w-0 rounded-md border border-gray-300 px-3 py-2 text-sm" placeholder="Shopee minimo" inputMode="decimal" />
            <div className="flex gap-2"><button type="button" onClick={createCampaignPriceRow} disabled={pricesLoading} className="flex-1 rounded-md bg-gray-900 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50">Salvar</button><button type="button" onClick={() => { setNewPrice(emptyNewPrice); setNewPriceOpen(false); }} className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-600">Cancelar</button></div>
          </div>
          <div className="mt-2 grid gap-2 md:grid-cols-3">
            <input value={newPrice.mlBasePrice} onChange={(e) => setNewPrice({ ...newPrice, mlBasePrice: e.target.value })} className="min-w-0 rounded-md border border-gray-300 px-3 py-2 text-sm" placeholder="Mercado Livre base" inputMode="decimal" />
            <input value={newPrice.mlCampaignPrice} onChange={(e) => setNewPrice({ ...newPrice, mlCampaignPrice: e.target.value })} className="min-w-0 rounded-md border border-gray-300 px-3 py-2 text-sm" placeholder="Mercado Livre campanha" inputMode="decimal" />
            <input value={newPrice.mlMinPrice} onChange={(e) => setNewPrice({ ...newPrice, mlMinPrice: e.target.value })} className="min-w-0 rounded-md border border-gray-300 px-3 py-2 text-sm" placeholder="Mercado Livre minimo" inputMode="decimal" />
          </div>
        </div>}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1160px] table-fixed divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-left text-xs font-semibold uppercase text-gray-500">
              <tr><th className="w-12 px-4 py-3"><input type="checkbox" checked={allVisiblePricesSelected} ref={(input) => { if (input) input.indeterminate = someVisiblePricesSelected && !allVisiblePricesSelected; }} onChange={(e) => toggleVisiblePriceSelection(e.target.checked)} aria-label="Selecionar pagina" /></th><th className="w-[260px] px-4 py-3">Produto</th><th className="w-[330px] px-4 py-3">Shopee</th><th className="w-[330px] px-4 py-3">Mercado Livre</th><th className="w-[208px] px-4 py-3">Vinculos</th></tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {pricesLoading && <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">Carregando tabela de precos...</td></tr>}
              {!pricesLoading && filteredCampaignPrices.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">Importe do Business Core para montar a tabela com os produtos e anuncios vinculados.</td></tr>}
              {!pricesLoading && visibleCampaignPrices.map((price) => {
                const shopeeIds = listingIds(price.shopeeListingLinks || []);
                const mlIds = listingIds(price.mlListingLinks || []);
                const selected = selectedPriceIds.has(price.id);
                const overrides = price.priceOverrides || {};
                const fromCutApp = price.priceSource === 'cutapp';
                const priceInput = (field: string, placeholder: string) => {
                  const locked = !!overrides[field];
                  return (
                    <div className="relative">
                      <input value={price[field] ?? ''} onChange={(e) => setLocalPrice(price.id, field, e.target.value)} onBlur={(e) => savePriceField(price.id, field, e.target.value).catch((err) => setPriceError(err.message))} className={`w-[96px] rounded-md border px-2 py-1.5 pr-6 text-xs ${locked ? 'border-amber-400 bg-amber-50' : 'border-gray-300'}`} inputMode="decimal" placeholder={placeholder} />
                      <button
                        type="button"
                        onClick={() => togglePriceOverride(price, field)}
                        title={showCutappUi
                          ? (locked ? 'Campo travado: a sincronizacao de precos (inclusive o Cut App) nao sobrescreve. Clique para destravar.' : 'Travar campo para a sincronizacao de precos (inclusive o Cut App) nao sobrescrever.')
                          : (locked ? 'Campo travado: a sincronizacao de precos nao sobrescreve. Clique para destravar.' : 'Travar campo para a sincronizacao de precos nao sobrescrever.')}
                        aria-label={locked ? `Destravar ${placeholder}` : `Travar ${placeholder}`}
                        className={`absolute right-1 top-1/2 -translate-y-1/2 text-[11px] leading-none ${locked ? 'text-amber-600' : 'text-gray-300 hover:text-gray-500'}`}
                      >
                        {locked ? '🔒' : '🔓'}
                      </button>
                    </div>
                  );
                };
                return (
                  <tr key={price.id} className={`align-top ${selected ? 'bg-blue-50/40' : ''}`}>
                    <td className="px-4 py-3"><input type="checkbox" checked={selected} onChange={(e) => togglePriceSelection(price.id, e.target.checked)} aria-label={`Selecionar ${price.sku || price.name}`} /></td>
                    <td className="px-4 py-3"><div className="whitespace-normal break-words font-medium leading-snug text-gray-900">{price.name || price.sku}</div><div className="mt-1 break-all text-xs text-gray-500">SKU {price.sku}</div>{fromCutApp && <span title={price.cutappSyncedAt ? `Sincronizado do Cut App em ${new Date(price.cutappSyncedAt).toLocaleString('pt-BR')}` : 'Precos vindos do Cut App'} className="mt-1 inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-purple-700">Cut App</span>}</td>
                    <td className="px-4 py-3 whitespace-nowrap"><div className="mb-2 flex flex-nowrap gap-2 text-xs text-gray-500"><span>{(price.shopeeListingLinks || []).length} anuncio(s)</span><span>base {formatCurrency(price.shopeeBasePrice)}</span></div><div className="flex flex-nowrap gap-2">{priceInput('shopeeBasePrice', 'base')}{priceInput('shopeeCampaignPrice', 'campanha')}{priceInput('shopeeMinPrice', 'minimo')}</div></td>
                    <td className="px-4 py-3 whitespace-nowrap"><div className="mb-2 flex flex-nowrap gap-2 text-xs text-gray-500"><span>{(price.mlListingLinks || []).length} anuncio(s)</span><span>base {formatCurrency(price.mlBasePrice)}</span></div><div className="flex flex-nowrap gap-2">{priceInput('mlBasePrice', 'base')}{priceInput('mlCampaignPrice', 'campanha')}{priceInput('mlMinPrice', 'minimo')}</div></td>
                    <td className="px-4 py-3 whitespace-nowrap"><div className="mb-2 flex min-w-[76px] flex-nowrap items-center gap-2"><MarketplaceLogo marketplace="shopee" ids={shopeeIds} onClick={() => openLinkModal(price, 'shopee')} /><MarketplaceLogo marketplace="mercadolivre" ids={mlIds} onClick={() => openLinkModal(price, 'mercadolivre')} />{shopeeIds.length === 0 && mlIds.length === 0 && <span className="whitespace-nowrap text-xs text-gray-400">Sem vinculo</span>}</div>
                      {(['shopee', 'mercadolivre'] as Marketplace[]).map((mp) => {
                        const suppressed = suppressedListingLinks(price, mp);
                        if (suppressed.length === 0) return null;
                        const label = mp === 'shopee' ? 'Shopee' : 'Mercado Livre';
                        return (
                          <button
                            key={mp}
                            type="button"
                            onClick={() => openLinkModal(price, mp)}
                            title={`Removido manualmente: ${suppressed.map((l: any) => l.listingId).join(', ')}. O sync nao traz de volta sozinho — clique para reativar.`}
                            className="mb-2 flex items-center gap-1 rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 hover:bg-amber-100"
                          >
                            {suppressed.length} vinculo {label} removido
                          </button>
                        );
                      })}<label className="flex items-center gap-2 text-xs text-gray-600"><input type="checkbox" checked={price.enabled !== false} onChange={(e) => { setLocalPrice(price.id, 'enabled', e.target.checked); savePriceField(price.id, 'enabled', e.target.checked).catch((err) => setPriceError(err.message)); }} />Ativo</label><button type="button" onClick={() => deleteCampaignPriceRow(price.id)} className="mt-2 text-xs font-medium text-red-600 hover:text-red-700">Remover</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!pricesLoading && filteredCampaignPrices.length > 0 && <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-4 py-3 text-xs text-gray-500">
          <span>Mostrando {pricePageStart}-{pricePageEnd} de {filteredCampaignPrices.length} linhas filtradas.</span>
          {pricePageCount > 1 && <div className="flex items-center gap-2">
            <button type="button" onClick={() => setPricePage((page) => Math.max(1, page - 1))} disabled={currentPricePage <= 1} className="rounded-md border border-gray-300 px-3 py-1.5 font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40">Anterior</button>
            <span className="font-medium text-gray-700">Pagina {currentPricePage} de {pricePageCount}</span>
            <button type="button" onClick={() => setPricePage((page) => Math.min(pricePageCount, page + 1))} disabled={currentPricePage >= pricePageCount} className="rounded-md border border-gray-300 px-3 py-1.5 font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40">Proxima</button>
          </div>}
        </div>}
      </section>
      </>}

      {activeTab === 'history' && renderJoinHistory()}

      {activeTab === 'campaigns' && <>
      <div className="grid min-w-0 gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
        <section className="min-w-0 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <select value={connectionId} onChange={(e) => setConnectionId(e.target.value)} className="w-full min-w-0 rounded-md border border-gray-300 px-3 py-2 text-sm md:min-w-80">
              <option value="">Todas conexoes</option>
              {connections.length === 0 && <option value="">Nenhuma conexao encontrada</option>}
              {connections.map((c) => <option key={`${c.marketplace}-${c.id}`} value={c.id}>{c.label}</option>)}
            </select>
            <div className="text-right text-xs text-gray-500">
              <div>Exibindo campanhas salvas no banco</div>
              <div>{settings?.lastCampaignSyncAt ? `Última sincronização: ${new Date(settings.lastCampaignSyncAt).toLocaleString('pt-BR')}` : 'Ainda não sincronizado'}</div>
            </div>
          </div>

          {error && <div className="mb-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
          {loading ? <div className="py-10 text-center text-sm text-gray-500">Carregando promocoes...</div> : (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 text-left text-xs font-semibold uppercase text-gray-500">
                  <tr><th className="px-4 py-3">Promocao</th><th className="px-4 py-3">Tipo</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Periodo</th><th className="px-4 py-3">Itens</th></tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {visiblePromotions.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">Nenhuma promocao ativa ou agendada encontrada.</td></tr>}
                  {visiblePromotions.map((promo: any) => (
                    <tr key={`${promo.marketplace}-${promo.connectionId}-${promo.type}-${promo.id}`}>
                      <td className="px-4 py-3"><div className="font-medium text-gray-900">{promo.name}</div><div className="text-xs text-gray-500">{promo.connectionName}</div></td>
                      <td className="px-4 py-3 text-gray-700">{typeLabel(promo.type)}</td>
                      <td className="px-4 py-3"><span className={`rounded-full px-2 py-1 text-xs font-semibold ${promo.status === 'ongoing' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>{statusLabel(promo.status)}</span></td>
                      <td className="px-4 py-3 text-gray-600">{formatDate(promo.startTime)} - {formatDate(promo.endTime)}</td>
                      <td className="px-4 py-3 text-gray-600">{promo.listingCount || promo.itemCount || 0} anuncio{(promo.listingCount || promo.itemCount) === 1 ? '' : 's'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {expiredCount > 0 && <p className="mt-3 text-xs text-gray-500">{expiredCount} promocoes encerradas ocultas da lista principal.</p>}
        </section>

        <aside className="min-w-0 space-y-4">
          <div className="min-w-0 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <h2 className="mb-3 text-sm font-semibold text-gray-900">Nova promocao</h2>
            <div className="space-y-3">
              <select value={createConnectionId} onChange={(e) => setCreateConnectionId(e.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm">
                <option value="">Selecionar conexao</option>
                {connections.length === 0 && <option value="">Nenhuma conexao encontrada</option>}
                {connections.map((c) => <option key={`create-${c.marketplace}-${c.id}`} value={c.id}>{c.label}</option>)}
              </select>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" placeholder="Nome da promocao" />
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm">
                {createMarketplace === 'shopee' ? <><option value="discount">Desconto</option><option value="bundle_deal">Leve mais por menos</option><option value="flash_sale">Oferta relampago</option></> : <><option value="seller_campaign">Campanha propria</option><option value="volume_campaign">Leve mais por menos</option></>}
              </select>
              {form.type !== 'flash_sale' && <div className="grid grid-cols-2 gap-2"><input type="datetime-local" value={form.startDateTime} onChange={(e) => setForm({ ...form, startDateTime: e.target.value })} className="rounded-md border border-gray-300 px-3 py-2 text-sm" /><input type="datetime-local" value={form.endDateTime} onChange={(e) => setForm({ ...form, endDateTime: e.target.value })} className="rounded-md border border-gray-300 px-3 py-2 text-sm" /></div>}
              {createMarketplace === 'shopee' && form.type === 'discount' && <input value={form.discountValue} onChange={(e) => setForm({ ...form, discountValue: e.target.value })} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" placeholder="Desconto padrao para referencia" />}
              {createMarketplace === 'shopee' && form.type === 'bundle_deal' && <div className="space-y-2"><select value={form.ruleType} onChange={(e) => setForm({ ...form, ruleType: e.target.value })} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"><option value="">Tipo de regra</option><option value="2">% desconto</option><option value="3">Valor desconto</option><option value="1">Preco fixo</option></select>{bundleTiers.map((tier, index) => <div key={index} className="space-y-2 rounded-md border border-gray-100 p-2"><div className="grid grid-cols-2 gap-2"><input value={tier.minQty} onChange={(e) => setBundleTiers((items) => items.map((item, i) => i === index ? { ...item, minQty: e.target.value } : item))} className="min-w-0 rounded-md border border-gray-300 px-3 py-2 text-sm" placeholder="A partir de" /><input value={tier.discountValue} onChange={(e) => setBundleTiers((items) => items.map((item, i) => i === index ? { ...item, discountValue: e.target.value } : item))} className="min-w-0 rounded-md border border-gray-300 px-3 py-2 text-sm" placeholder={form.ruleType === '2' ? '% desconto' : form.ruleType === '3' ? 'Valor desconto' : 'Preco fixo'} /></div><button type="button" disabled={bundleTiers.length <= 1} onClick={() => setBundleTiers((items) => items.filter((_, i) => i !== index))} className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-xs font-medium text-gray-600 disabled:opacity-40">Remover nivel</button></div>)}<button type="button" disabled={bundleTiers.length >= 3} onClick={() => setBundleTiers((items) => [...items, { minQty: '', discountValue: '' }])} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 disabled:opacity-50">Adicionar nivel</button><input value={form.purchaseLimit} onChange={(e) => setForm({ ...form, purchaseLimit: e.target.value })} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" placeholder="Limite por comprador" /></div>}
              {createMarketplace === 'shopee' && form.type === 'flash_sale' && <div className="max-h-56 space-y-2 overflow-y-auto rounded-md border border-gray-200 p-2 text-xs">{loadingSlots && <div className="py-3 text-center text-gray-500">Carregando slots...</div>}{!loadingSlots && flashSlots.length === 0 && <div className="py-3 text-center text-gray-500">Nenhum slot encontrado nos proximos 7 dias.</div>}{flashSlots.map((slot) => { const selected = selectedFlashSlotId === String(slot.timeSlotId); return <button type="button" key={slot.timeSlotId} disabled={!slot.available} onClick={() => setSelectedFlashSlotId(String(slot.timeSlotId))} className={`w-full rounded-md border px-2 py-2 text-left ${selected ? 'border-orange-500 bg-orange-50 text-orange-800' : slot.available ? 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:border-emerald-400' : 'border-gray-200 bg-gray-50 text-gray-500 opacity-70'}`}><div className="font-medium">{new Date(slot.startTime * 1000).toLocaleString('pt-BR')} - {new Date(slot.endTime * 1000).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</div><div>{selected ? 'Selecionado' : slot.available ? 'Disponivel para entrar/criar' : 'Ja existe campanha neste slot'}</div></button>; })}</div>}
              {createMarketplace === 'mercadolivre' && form.type === 'volume_campaign' && <div className="space-y-2"><select value={form.mlSubType} onChange={(e) => setForm({ ...form, mlSubType: e.target.value })} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"><option value="BNSP">Compre X com % desconto</option><option value="BNGM">Leve X pague Y</option><option value="SPONTH">% na segunda unidade</option></select><div className="grid grid-cols-2 gap-2"><input value={form.mlBuyQty} onChange={(e) => setForm({ ...form, mlBuyQty: e.target.value })} className="rounded-md border border-gray-300 px-3 py-2 text-sm" placeholder="Qtd. compra" />{form.mlSubType === 'BNGM' ? <input value={form.mlPayQty} onChange={(e) => setForm({ ...form, mlPayQty: e.target.value })} className="rounded-md border border-gray-300 px-3 py-2 text-sm" placeholder="Qtd. paga" /> : <input value={form.mlDiscountPct} onChange={(e) => setForm({ ...form, mlDiscountPct: e.target.value })} className="rounded-md border border-gray-300 px-3 py-2 text-sm" placeholder="% desconto" />}</div><div className="rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-600">Permitir combinacao: ativado automaticamente.</div></div>}
              <button disabled={saving || !createConnectionId || (form.type === 'flash_sale' ? !selectedFlashSlotId : (!form.name.trim() || !form.startDateTime || !form.endDateTime))} onClick={submit} className="w-full rounded-md bg-orange-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50">Criar promocao</button>
            </div>
          </div>

        </aside>
      </div>
      </>}

      {campaignCheckModal && (() => {
        const step = campaignCheckModal.step || 'idle';
        const result = campaignCheckModal.result || {};
        const link = result.link || {};
        const planned = Array.isArray(link.planned) ? link.planned : [];
        const skipped = Array.isArray(link.skipped) ? link.skipped : [];
        const failed = Array.isArray(link.failed) ? link.failed : [];
        const joined = Array.isArray(link.joined) ? link.joined : [];
        const rows = step === 'done' ? joined : planned;
        const priceIds = Array.isArray(campaignCheckModal.priceIds) ? campaignCheckModal.priceIds : [];
        const selectedProducts = campaignPrices.filter((price) => priceIds.includes(price.id));
        const selectedEntries = planned.filter((item: any) => campaignCheckSelections.has(campaignEntryKey(item)));
        const allPlannedSelected = planned.length > 0 && selectedEntries.length === planned.length;
        const toggleAllPlanned = () => setCampaignCheckSelections(allPlannedSelected ? new Set<string>() : new Set(planned.map(campaignEntryKey)));
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="flex max-h-[96vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
              <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
                <div>
                  <h2 className="text-base font-semibold text-gray-900">Verificar e aderir em campanhas</h2>
                  <p className="text-sm text-gray-500">{priceIds.length} produto(s) selecionado(s). O sistema consulta campanhas elegiveis e so adere depois da autorizacao.</p>
                </div>
                <button type="button" onClick={() => setCampaignCheckModal(null)} className="rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-600 hover:bg-gray-50">Fechar</button>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
                {step === 'idle' && <div className="space-y-4">
                  <div className="grid gap-3 md:grid-cols-2">
                    {selectedProducts.slice(0, 8).map((price) => {
                      const shopeeCount = (price.shopeeListingLinks || []).length;
                      const mlCount = (price.mlListingLinks || []).length;
                      return <div key={price.id} className="rounded-md border border-gray-200 bg-gray-50 px-3 py-3">
                        <div className="break-words text-sm font-semibold leading-snug text-gray-900">{price.name || price.sku}</div>
                        <div className="mt-1 text-xs text-gray-500">SKU {price.sku}</div>
                        <div className="mt-2 flex flex-wrap gap-2 text-xs text-gray-600">
                          {shopeeCount > 0 && <span className="rounded bg-orange-50 px-2 py-1 text-orange-700">Shopee: {shopeeCount} anuncio(s)</span>}
                          {mlCount > 0 && <span className="rounded bg-yellow-50 px-2 py-1 text-yellow-700">Mercado Livre: {mlCount} anuncio(s)</span>}
                          {shopeeCount === 0 && mlCount === 0 && <span className="rounded bg-amber-50 px-2 py-1 text-amber-700">Sem anuncios vinculados</span>}
                        </div>
                      </div>;
                    })}
                    {selectedProducts.length > 8 && <div className="flex items-center justify-center rounded-md border border-dashed border-gray-300 px-3 py-3 text-sm text-gray-500">+{selectedProducts.length - 8} produto(s)</div>}
                  </div>
                </div>}

                {(step === 'checking' || step === 'joining') && <div className="flex flex-col items-center justify-center gap-4 py-20 text-center">
                  <div className="h-12 w-12 animate-spin rounded-full border-4 border-orange-100 border-t-orange-600" />
                  <div>
                    <p className="text-base font-semibold text-gray-900">{step === 'checking' ? 'Verificando campanhas...' : 'Aderindo nas campanhas...'}</p>
                    <p className="mt-1 text-sm text-gray-500">{step === 'checking' ? 'Consultando Shopee e Mercado Livre no backend.' : 'Enviando apenas as entradas autorizadas.'}</p>
                  </div>
                </div>}

                {(step === 'results' || step === 'done') && <>
                  {link.truncated === 'deadline' && <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    A verificação atingiu o tempo limite e parou antes de percorrer todos os produtos. O que aparece abaixo está correto, mas está incompleto — rode de novo com menos produtos selecionados para cobrir o restante.
                  </div>}
                  {link.truncated === 'max_per_run' && <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    A verificação parou no limite de adesões por rodada. O que aparece abaixo está correto, mas está incompleto — aumente o limite nas configurações ou rode de novo para cobrir o restante.
                  </div>}
                  <div className="mb-3 grid grid-cols-3 gap-2 text-center text-xs">
                    <div className="rounded-md bg-emerald-50 px-2 py-2 text-emerald-700"><div className="font-semibold">{step === 'done' ? joined.length : planned.length}</div><div>{step === 'done' ? 'Adesoes' : 'Elegiveis'}</div></div>
                    <div className="rounded-md bg-gray-50 px-2 py-2 text-gray-600"><div className="font-semibold">{skipped.length}</div><div>Ignoradas</div></div>
                    <div className="rounded-md bg-red-50 px-2 py-2 text-red-700"><div className="font-semibold">{failed.length}</div><div>Falhas</div></div>
                  </div>
                  {step === 'results' && planned.length > 0 && <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-orange-100 bg-orange-50 px-3 py-2">
                    <span className="text-sm font-medium text-orange-800">{selectedEntries.length} de {planned.length} entrada(s) selecionada(s) para adesao</span>
                    <button type="button" onClick={toggleAllPlanned} className="rounded-md border border-orange-200 bg-white px-3 py-1.5 text-xs font-semibold text-orange-700 hover:bg-orange-50">{allPlannedSelected ? 'Desmarcar tudo' : 'Selecionar tudo'}</button>
                  </div>}
                  {rows.length === 0 && <div className="rounded-md bg-gray-50 px-3 py-6 text-center text-sm text-gray-500">Nenhuma campanha elegivel encontrada agora.</div>}
                  {rows.length > 0 && <div className="overflow-x-auto rounded-md border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50 text-left text-xs font-semibold uppercase text-gray-500">
                        <tr>{step === 'results' && <th className="w-10 px-3 py-2">Sel.</th>}<th className="px-3 py-2">Produto</th><th className="px-3 py-2">Canal</th><th className="px-3 py-2">Campanha</th><th className="px-3 py-2">Anuncio</th><th className="px-3 py-2">Preco</th></tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white">
                        {rows.map((item: any, index: number) => {
                          const key = campaignEntryKey(item);
                          const checked = campaignCheckSelections.has(key);
                          return <tr key={`${key}-${index}`} className={step === 'results' && checked ? 'bg-orange-50/40' : ''}>
                            {step === 'results' && <td className="px-3 py-2"><input type="checkbox" checked={checked} onChange={(event) => setCampaignCheckSelections((current) => { const next = new Set(current); if (event.target.checked) next.add(key); else next.delete(key); return next; })} aria-label="Selecionar entrada" /></td>}
                            <td className="px-3 py-2"><div className="font-medium text-gray-900">{item.productName || item.sku || item.listingId}</div><div className="text-xs text-gray-500">{item.sku ? `SKU ${item.sku}` : '-'}</div></td>
                            <td className="px-3 py-2 text-gray-700">{marketplaceLabel(item.marketplace)}</td>
                            <td className="px-3 py-2"><div className="text-gray-900">{item.campaignName || item.campaignId || typeLabel(item.type)}</div><div className="text-xs text-gray-500">{typeLabel(item.type)}</div></td>
                            <td className="px-3 py-2 text-gray-700">{[item.listingId, item.variationId ? `var ${item.variationId}` : null, item.modelId ? `modelo ${item.modelId}` : null].filter(Boolean).join(' / ')}</td>
                            <td className="px-3 py-2 text-gray-700">{formatCurrency(item.price)}</td>
                          </tr>;
                        })}
                      </tbody>
                    </table>
                  </div>}
                  {(skipped.length > 0 || failed.length > 0) && <div className="mt-3 max-h-36 overflow-y-auto rounded-md border border-gray-200 bg-gray-50 p-2 text-xs text-gray-700">
                    {[...failed, ...skipped].slice(0, 30).map((item: any, index: number) => <div key={index} className="border-b border-gray-200 py-1 last:border-0">{resultLine(item)}</div>)}
                  </div>}
                </>}
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 px-5 py-4">
                <span className="text-sm text-gray-500">{step === 'idle' ? 'Aguardando verificacao' : step === 'done' ? 'Concluido' : `${selectedEntries.length} entrada(s) selecionada(s)`}</span>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setCampaignCheckModal(null)} className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">{step === 'done' ? 'Fechar' : 'Cancelar'}</button>
                  {step === 'idle' && <button type="button" onClick={runCampaignCheckPreview} disabled={saving || priceIds.length === 0} className="rounded-md bg-orange-600 px-3 py-2 text-sm font-semibold text-white hover:bg-orange-700 disabled:opacity-50">Verificar campanhas disponiveis</button>}
                  {step === 'results' && planned.length > 0 && <button type="button" onClick={authorizeCampaignCheck} disabled={saving || selectedEntries.length === 0} className="rounded-md bg-orange-600 px-3 py-2 text-sm font-semibold text-white hover:bg-orange-700 disabled:opacity-50">{saving ? 'Entrando...' : `Aderir em ${selectedEntries.length} campanha(s)`}</button>}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {basePricePromoModal && (() => {
        const listings = basePricePromoModal.listings;
        const withPromotions = listings.filter((item: any) => (item.promotions || []).length > 0);
        const checkErrors = listings.filter((item: any) => item.checkError);
        const lockedCount = withPromotions.filter((item: any) => item.promotions.some((promo: any) => !promo.removable)).length;
        const close = () => setBasePricePromoModal(null);
        const proceed = (removePromotions: boolean) => {
          const ids = basePricePromoModal.ids;
          setBasePricePromoModal(null);
          runBasePriceUpdate(ids, removePromotions);
        };
        const statusLabel = (status: string) => ({ ongoing: 'em andamento', started: 'ativa', upcoming: 'programada', pending: 'programada', programmed: 'programada', active: 'ativa' } as Record<string, string>)[String(status || '').toLowerCase()] || status || '';
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="flex max-h-[96vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
              <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
                <div>
                  <h2 className="text-base font-semibold text-gray-900">Anuncios em promocao</h2>
                  <p className="text-sm text-gray-500">A Shopee e o Mercado Livre nao aceitam alterar o preco Base de um anuncio em promocao. Para atualizar, o sistema precisa remover estes anuncios das promocoes abaixo. As promocoes removidas nao sao readicionadas automaticamente.</p>
                </div>
                <button type="button" onClick={close} className="rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-600 hover:bg-gray-50">Fechar</button>
              </div>
              <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-4">
                {withPromotions.map((item: any, index: number) => (
                  <div key={`${item.id}-${item.marketplace}-${item.listingId}-${item.modelId || item.variationId || ''}-${index}`} className="rounded-md border border-gray-200 bg-gray-50 px-3 py-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded px-2 py-0.5 text-xs font-medium ${item.marketplace === 'shopee' ? 'bg-orange-50 text-orange-700' : 'bg-yellow-50 text-yellow-800'}`}>{item.marketplace === 'shopee' ? 'Shopee' : 'Mercado Livre'}</span>
                      <span className="break-words text-sm font-semibold text-gray-900">{item.name || item.sku}</span>
                    </div>
                    <div className="mt-1 text-xs text-gray-500">SKU {item.sku} · anuncio {item.listingId}{item.modelId ? ` · modelo ${item.modelId}` : ''}{item.variationId && !item.modelId ? ` · variacao ${item.variationId}` : ''}</div>
                    <ul className="mt-2 space-y-1">
                      {item.promotions.map((promo: any, promoIndex: number) => (
                        <li key={promoIndex} className="text-xs text-gray-700">
                          <span className="font-medium">{promo.label}</span>
                          {promo.promotionId ? <span className="text-gray-500"> #{promo.promotionId}</span> : null}
                          {promo.status ? <span className="text-gray-500"> · {statusLabel(promo.status)}</span> : null}
                          {!promo.removable && <div className="mt-0.5 rounded bg-red-50 px-2 py-1 text-red-700">Nao pode ser removida pelo sistema: {promo.reason}</div>}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                {checkErrors.length > 0 && <div className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  Nao foi possivel verificar {checkErrors.length} anuncio(s): {checkErrors.slice(0, 3).map((item: any) => `${item.sku} (${item.listingId}): ${item.checkError}`).join(' | ')}
                </div>}
                {lockedCount > 0 && <div className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">
                  {lockedCount} anuncio(s) tem promocao que o sistema nao consegue remover. O marketplace pode recusar o novo preco Base desses anuncios.
                </div>}
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-200 px-5 py-4">
                <button type="button" onClick={close} className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancelar</button>
                <button type="button" onClick={() => proceed(false)} className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Atualizar so os que nao estao em promocao</button>
                <button type="button" onClick={() => proceed(true)} disabled={withPromotions.length === 0} className="rounded-md bg-orange-600 px-3 py-2 text-sm font-semibold text-white hover:bg-orange-700 disabled:opacity-50">Remover das promocoes e atualizar</button>
              </div>
            </div>
          </div>
        );
      })()}

      {linkModal && (() => {
        const marketplaceName = linkModal.marketplace === 'shopee' ? 'Shopee' : 'Mercado Livre';
        const links = linkModal.links;
        const selectedCount = linkModal.selectedKeys.length;
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-2xl rounded-lg bg-white shadow-xl">
              <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
                <div>
                  <h2 className="text-base font-semibold text-gray-900">Editar vinculos {marketplaceName}</h2>
                  <p className="text-sm text-gray-500">{linkModal.price.name || linkModal.price.sku}</p>
                </div>
                <button type="button" onClick={() => setLinkModal(null)} className="rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-600 hover:bg-gray-50">Fechar</button>
              </div>
              <div className="max-h-[60vh] overflow-y-auto px-5 py-4">
                {links.length === 0 && <div className="rounded-md bg-gray-50 px-3 py-6 text-center text-sm text-gray-500">Nenhum anuncio disponivel para este marketplace.</div>}
                {links.length > 0 && <div className="space-y-2">
                  {links.map((link: any) => {
                    const key = campaignLinkKey(link);
                    const checked = linkModal.selectedKeys.includes(key);
                    const idLine = [link.listingId, link.variationId ? `var ${link.variationId}` : null, link.modelId ? `modelo ${link.modelId}` : null].filter(Boolean).join(' / ');
                    return (
                      <label key={key} className={`flex cursor-pointer items-start gap-3 rounded-md border px-3 py-3 ${checked ? 'border-emerald-300 bg-emerald-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => setLinkModal((current) => {
                            if (!current) return current;
                            const next = new Set(current.selectedKeys);
                            if (e.target.checked) next.add(key); else next.delete(key);
                            return { ...current, selectedKeys: Array.from(next) };
                          })}
                          className="mt-1 h-4 w-4"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium text-gray-900">{link.title || idLine}</div>
                          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500"><span>ID {idLine}</span>{link.connectionName && <span>{link.connectionName}</span>}{link.price != null && <span>{formatCurrency(link.price)}</span>}</div>
                          {link.url && <div className="mt-1 truncate text-xs text-gray-400">{link.url}</div>}
                        </div>
                      </label>
                    );
                  })}
                </div>}
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 px-5 py-4">
                <span className="text-sm text-gray-500">{selectedCount} de {links.length} anuncio(s) vinculados</span>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setLinkModal(null)} className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancelar</button>
                  <button type="button" onClick={saveLinkModal} className="rounded-md bg-gray-900 px-3 py-2 text-sm font-semibold text-white hover:bg-gray-800">Salvar vinculos</button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
